import { useMemo } from 'react'
import useFilterUtils from 'components/Filter/useFilterUtils'
import useHistoryUtils from 'hooks/useHistoryUtils'
import useRolesViews from 'hooks/useRolesViews'
import useStateBackLink from 'hooks/useStateBackLink'
import { absenceRequestStatus } from 'pages/Tasks/AbsenceRequest/constants'
import { updateAbsenceRequestMutationApollo as rejectMutation } from 'pages/Tasks/AbsenceRequest/mutations'
import { deleteTaskQuery } from 'pages/Tasks/queries'
import type { TaskT, TaskDataT, AbsenceRequestTaskT } from 'pages/Tasks/types'
import { SNACKBAR_VARIANTS } from 'redesign/components/atoms/snackbar-base/constants'
import { useToastContext } from 'redesign/services/toast/use-toast-context'
import routes from 'services/RoutesProvider/routes'
import type { ApproveAbsenceRequestResultT, PersonT } from 'types'
import type { MutationResult } from '@apollo/client'
import { useMutation } from '@apollo/client'
import { getPersonId } from '../buttons/delete-workflow-button/utils/get-person-id'
import { BUTTON_TYPE } from '../constant'
import type { UseActionHandlerT } from '../types'
import { useAcceptMultipleMonths } from './use-accept-multiple-months'

export const useActionHandler = ({
  task,
  onDelete,
  prevMonthTask,
  setDialogProps,
  refetch,
  sendEvent,
  eventLoading,
  t,
  setAbsencePlannerDrawerProps,
  isSmallScreen,
}: UseActionHandlerT) => {
  const { setLoadFilterTasks } = useFilterUtils()
  const { persons } = useRolesViews()
  const { pushWithRole } = useHistoryUtils()
  const personId = useMemo(() => getPersonId(persons, task as TaskT<TaskDataT>), [persons, task])
  const [deleteTaskMutation] = useMutation<MutationResult>(deleteTaskQuery)
  const [rejectAbsenceRequest] = useMutation<ApproveAbsenceRequestResultT>(rejectMutation)
  const { openToast } = useToastContext()
  const { pushBackLinkOrDefault } = useStateBackLink()
  const { handleAcceptMultiple } = useAcceptMultipleMonths({
    prevMonthTask,
    setDialogProps,
    refetch,
    sendEvent,
    eventLoading,
    t,
  })

  const reload = () => {
    setLoadFilterTasks()
    pushBackLinkOrDefault(routes.tasks)
  }

  const handleDeleteWorkflow = () => {
    if (!personId) return openToast({ title: t('common.error.label.errorTitle'), variant: SNACKBAR_VARIANTS.error })

    return deleteTaskMutation({ variables: { favurUuid: task.favurUuid, personId } })
      .then(() => {
        if (onDelete) {
          onDelete()

          return
        }

        reload()
      })
      .catch((_e) => {
        openToast({ title: t('common.error.be.default'), variant: SNACKBAR_VARIANTS.error })
      })
  }

  const handleRejectAbsenceRequest = async (commentText?: string) => {
    const mutationResult = await rejectAbsenceRequest({
      variables: {
        reviewerComment: commentText,
        status: absenceRequestStatus.rejectedOffice,
        taskUuid: task.favurUuid,
      },
    })

    if (mutationResult.data?.updateAbsenceStatus.success) {
      refetch()
      openToast({ title: t('confirmation.answerSent'), variant: SNACKBAR_VARIANTS.success })
    }
  }

  const handleDisplayAbsencePlannerDrawer = async () => {
    const absencePlannerTask = task as TaskT<AbsenceRequestTaskT>
    const startDate = absencePlannerTask.taskData.dateFrom
    const endDate = absencePlannerTask.taskData.dateTo

    if (isSmallScreen) {
      setAbsencePlannerDrawerProps({
        previewPerson: absencePlannerTask.person as PersonT,
        startDate,
        endDate,
      })

      return
    }

    pushWithRole(`${routes.absencePlanner}?startDate=${startDate}&endDate=${endDate}`)
  }

  const handleButtonAction = (buttonType: keyof typeof BUTTON_TYPE, commentText?: string) => {
    const actionMap = {
      [BUTTON_TYPE.acceptMultipleMonths]: handleAcceptMultiple,
      [BUTTON_TYPE.delete]: handleDeleteWorkflow,
      [BUTTON_TYPE.displayAbsencePlannerDrawer]: handleDisplayAbsencePlannerDrawer,
      [BUTTON_TYPE.rejectAbsenceRequest]: handleRejectAbsenceRequest,
    }

    const action = actionMap[buttonType]

    if (action) {
      action(commentText)
    }
  }

  return { handleButtonAction }
}
